import { ShareScreenBase } from "./share-screen-base"
import { RoomContentCard } from "./room-content-card"
import { getRoomDeeplinkUrl, isMobile, isUsOrCaTimezone } from "~/common/platform"
import { getMetaContent } from "~/common/get-meta-content"
import { useDocumentTitleDescription } from "~/common/use-document-title"
import { getFeedMessages, getInviterData, getRoomData } from "~/share/share-embedded-data"
import { useRoomSharePhoneSubmission } from "~/hooks/use-phone-submission"
import { useCallback } from "react"
import { EVENT_IDS, SCREENS } from "~/common/analytics"
import { useScreenTracking } from "~/hooks/use-screen-tracking"
import { MessageFeed } from "./components/message-feed"
import { RoomHeader } from "./components/room-header"
import { useHeaderCollapse } from "./use-header-collapse"
import { useFeedEnabled } from "./use-feed-enabled"

export const ShareRoomScreen = (): JSX.Element => {
  const roomData = getRoomData()
  const rawFeedMessages = getFeedMessages()
  const feedEnabled = useFeedEnabled(!!rawFeedMessages)
  const feedMessages = feedEnabled ? rawFeedMessages : null

  const appEnvironment = getMetaContent<string>("APP_ENVIRONMENT")
  const deeplinkUrl = getRoomDeeplinkUrl(appEnvironment, roomData.id)
  const roomTitle = [roomData.name, "on ChatBCC"].join(" ")
  useDocumentTitleDescription(roomTitle, roomData.description ?? null)

  const { phoneValue, setPhoneValue, submitPhoneNumber } = useRoomSharePhoneSubmission()

  const roomId = roomData.id
  const roomHandle = roomData.roomHandle ?? undefined

  useScreenTracking(SCREENS.Share, {
    room_id: roomId,
    room_handle: roomHandle,
    deeplink_url: deeplinkUrl,
  })

  const { headerExpanded, handleNearTop, handleToggleExpand } = useHeaderCollapse()

  const handleJoinRoom = useCallback(async () => {
    const successMessage =
      isMobile() || !isUsOrCaTimezone()
        ? "Download the app to join the group chat."
        : "Check your texts for a link to download the app."

    const successTitle = "You skipped the waitlist!"
    const inviterData = getInviterData()

    await submitPhoneNumber({
      successMessage,
      successTitle,
      roomId: roomId,
      trackingData: {
        unique_id: EVENT_IDS.SHARE_SCREEN_SKIP_WAITLIST,
        inviter_id: inviterData?.id,
        inviter_username: inviterData?.username,
        room_id: roomId,
        deeplink_url: deeplinkUrl,
        room_handle: roomHandle,
      },
    })
  }, [submitPhoneNumber, deeplinkUrl, roomHandle, roomId])

  return (
    <ShareScreenBase
      deeplinkUrl={deeplinkUrl}
      handleJoinRoom={handleJoinRoom}
      analyticsProps={{
        room_id: roomId,
        room_handle: roomHandle,
      }}
      phoneValue={phoneValue}
      setPhoneValue={setPhoneValue}
      renderFeedHeader={
        feedMessages
          ? ({ desktop, logoSlot }) => (
              <RoomHeader
                roomData={roomData}
                expanded={headerExpanded}
                variant={desktop ? "full" : "compact"}
                logoSlot={logoSlot}
                onToggleExpand={handleToggleExpand}
              />
            )
          : undefined
      }
      renderFeedMessages={
        feedMessages
          ? ({ phoneInputRef, variant, contentPaddingTop }) => (
              <MessageFeed
                messages={feedMessages}
                onNearTop={handleNearTop}
                onAtBottom={handleToggleExpand}
                onCtaClick={() => phoneInputRef.current?.focus()}
                variant={variant}
                contentPaddingTop={contentPaddingTop}
              />
            )
          : undefined
      }
      renderContent={() =>
        roomData && <RoomContentCard roomData={roomData} className="max-w-[640px]" />
      }
    />
  )
}

export default ShareRoomScreen
